import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";

/**
 * FacturarModal — modal ÚNICO para facturar (33) desde la OT, desde Producción → Por
 * regularizar y desde una factura externa ya sincronizada. Permite editar la qty a
 * facturar por línea (default = qty pendiente). Opcionalmente aplica el mismo
 * clientOCNumber a todas las COs implicadas.
 *
 * Props:
 *   fuente  "regularizar" (por defecto) | "ot" | "externa"
 *   target  según la fuente (ojo: pasar un objeto ESTABLE, guardado en un estado; si cambia
 *           la identidad en cada render el modal vuelve a cargar):
 *     · "regularizar": { lines: [{ piId, deliveryId, sku, name, pendingQty, qty?, unitPrice,
 *                        guiaNumber, coId, coNumber, existingClientOC, clientId, clientName }] }
 *     · "ot":          { woNumber, nombre? } — carga GET dte/emitible/:woNumber y emite con
 *                      POST dte/emitir { woNumber, items, origen:"ot", tipoDoc:33 }.
 *                      Solo modo electrónica (una OT sin guía no tiene folio externo que registrar).
 *     · "externa":     { factura: { _id, folio, tipoDoc, fecha, rutReceptor, razonReceptor,
 *                      montoTotal, clienteId, clienteNombre } } — factura de Enternet ya traída por
 *                      el sync del SII; abre directo en modo "externo" con folio y fecha fijos y
 *                      lista las guías por regularizar del cliente para vincularlas.
 *   onClose, onSaved
 *
 * Backend:
 *   · "electronica" (26-oct, camino principal): POST /dte/previsualizar-desde-guias para el
 *     borrador y POST /dte/emitir-desde-guias para emitir la 33 por SimpleFactura. Si las
 *     líneas son de varias OT, la factura queda con repartos por OT y descuenta las
 *     entregas de esas guías. (fuente "ot": el borrador se calcula en el cliente y se emite
 *     con POST /dte/emitir.)
 *   · "externo" (secundario, marcha blanca con Enternet): POST /produccion/regularizar-factura
 *     con el folio tipeado (o el de la factura, en fuente "externa").
 */
const fmtCLP = (n) => new Intl.NumberFormat("es-CL", {
  style: "currency", currency: "CLP", maximumFractionDigits: 0,
}).format(n || 0);

const FacturarModal = ({ fuente = "regularizar", target, onClose, onSaved }) => {
  // Móvil (390 px): el modal entero hace scroll (no cada bloque) y la tabla esconde las
  // columnas secundarias para que entren SKU, descripción y la cantidad a facturar.
  const isMobile = useIsMobile();
  const esOt = fuente === "ot";
  const esExterna = fuente === "externa";
  const facturaExt = esExterna ? (target?.factura || null) : null;
  const [modo, setModo]               = useState("electronica"); // electronica | externo
  const [prev, setPrev]               = useState(null);   // borrador de /dte/previsualizar-desde-guias
  const [prevError, setPrevError]     = useState("");
  const [emitido, setEmitido]         = useState(null);   // respuesta de /dte/emitir-desde-guias
  const [docType, setDocType]         = useState("factura");
  const [docNumber, setDocNumber]     = useState("");
  const [docDate, setDocDate]         = useState("");
  const [clientOC, setClientOC]       = useState("");
  const [qtyById, setQtyById]         = useState({}); // deliveryId → qty
  const [ocFile, setOcFile]           = useState(null);
  const [saving, setSaving]           = useState(false);
  const [savingStep, setSavingStep]   = useState(""); // texto de estado durante submit
  const [error, setError]             = useState("");
  // Fuentes "ot" y "externa": las líneas se cargan del backend al abrir.
  const [cargadas, setCargadas]       = useState([]);
  const [cargando, setCargando]       = useState(false);
  const [cargaError, setCargaError]   = useState("");
  const [otInfo, setOtInfo]           = useState(null);   // { wo, receptor, esProduccion } de dte/emitible
  const [clienteSinId, setClienteSinId] = useState(false); // externa: no se pudo identificar al cliente

  // Inicializar al abrir
  useEffect(() => {
    if (!target) return;
    const fx = fuente === "externa" ? target.factura : null;
    setDocType(fx && Number(fx.tipoDoc) === 61 ? "nota_credito" : "factura");
    setDocNumber(fx ? String(fx.folio ?? "") : "");
    setDocDate(fx?.fecha ? String(fx.fecha).slice(0, 10) : new Date().toISOString().slice(0, 10));
    setError("");
    // qty default: la que viene desde la selección (puede ser parcial si Catalina
    // ya la editó inline); fallback al pendingQty completo.
    const initial = {};
    (target.lines || []).forEach(ln => {
      initial[ln.deliveryId] = ln.qty != null ? ln.qty : ln.pendingQty;
    });
    setQtyById(initial);
    // Si todas las líneas pertenecen a COs con la MISMA clientOCNumber ya
    // asignada, prellenamos ese N°. Si están mezcladas o sin OC, vacío.
    const ocs = [...new Set((target.lines || [])
      .map(l => l.existingClientOC)
      .filter(Boolean))];
    setClientOC(ocs.length === 1 ? ocs[0] : "");
    setOcFile(null);
    setSavingStep("");
    // La factura externa ya existe: abre directo en folio externo, sin chip para cambiar.
    setModo(fuente === "externa" ? "externo" : "electronica");
    setPrev(null); setPrevError(""); setEmitido(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // Fuentes "ot" y "externa": carga de las líneas al abrir.
  useEffect(() => {
    if (!target || fuente === "regularizar") return undefined;
    let vivo = true;
    setCargando(true); setCargaError(""); setCargadas([]); setOtInfo(null);
    setClienteSinId(false); setQtyById({});
    const cargar = async () => {
      try {
        if (fuente === "ot") {
          const { data: d } = await backend.get(`dte/emitible/${target.woNumber}`);
          if (!vivo) return;
          const ls = (d.items || [])
            .filter(it => Number(it.saldo) > 0)
            .map(it => ({
              piId: it.piId, deliveryId: it.piId, // el piId es la clave de fila: no hay entrega
              sku: it.sku, name: it.nombre,
              pendingQty: it.saldo, qty: it.saldo, unitPrice: it.precio,
              guiaNumber: "OT " + d.wo?.number, coNumber: d.wo?.number,
              clientId: d.receptor?._id || null, clientName: d.receptor?.name || null,
            }));
          setOtInfo({ wo: d.wo, receptor: d.receptor, esProduccion: !!d.esProduccion });
          setCargadas(ls);
          const q = {};
          ls.forEach(l => { q[l.deliveryId] = l.pendingQty; });
          setQtyById(q);
        } else {
          // Misma fuente que Producción → Por regularizar (GET produccion/issues).
          const { data } = await backend.get("produccion/issues?card=porRegularizar");
          if (!vivo) return;
          const cid = target.factura?.clienteId ? String(target.factura.clienteId) : null;
          // Sin cliente resuelto por RUT no se ofrece NINGUNA guía: así no se vincula la factura a
          // piezas de otro cliente (revisor 4-nov). El backend lo vuelve a comprobar por RUT.
          const guias = cid ? (data?.items || []).filter(g => String(g.client?._id) === cid) : [];
          const ls = [];
          guias.forEach(g => (g.items || []).forEach(it => {
            if (!(Number(it.pendingQty) > 0)) return;
            ls.push({
              piId: it.piId, deliveryId: it.deliveryId, sku: it.sku, name: it.name,
              pendingQty: it.pendingQty, qty: 0, unitPrice: it.unitPrice,
              guiaNumber: g.guia,
              coId: g.customerOrder?._id || null,
              coNumber: g.customerOrder?.number || null,
              existingClientOC: g.customerOrder?.clientOCNumber || null,
              clientId: g.client?._id || null,
              clientName: g.client?.name || null,
            });
          }));
          setClienteSinId(!cid);
          setCargadas(ls);
          // Parte en 0: quien vincula elige qué guías cubre el folio (botón "Toda la guía").
          const q = {};
          ls.forEach(l => { q[l.deliveryId] = 0; });
          setQtyById(q);
        }
      } catch (e) {
        if (vivo) setCargaError(e?.response?.data?.error || e.message || "No se pudo cargar");
      }
      if (vivo) setCargando(false);
    };
    cargar();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, fuente]);

  // Memorizado: sin esto cada render creaba un [] nuevo y los useMemo de abajo se
  // recalculaban igual (lo advertía el build).
  const lines = useMemo(
    () => (fuente === "regularizar" ? (target?.lines || []) : cargadas),
    [fuente, target, cargadas]
  );
  const ocConflict = useMemo(() => {
    const ocs = [...new Set(lines.map(l => l.existingClientOC).filter(Boolean))];
    return ocs.length > 1;
  }, [lines]);

  // Un DTE tiene un solo receptor: con clientes distintos la electrónica se bloquea.
  const clientes = useMemo(() => {
    const m = new Map();
    for (const ln of lines) if (ln.clientId) m.set(String(ln.clientId), ln.clientName || "cliente");
    return [...m.values()];
  }, [lines]);
  const clienteUnico = clientes.length <= 1;

  const payloadLinesDe = () => lines
    .map(ln => ({ piId: ln.piId, deliveryId: ln.deliveryId, qty: Math.round(Number(qtyById[ln.deliveryId] || 0)) }))
    .filter(l => l.qty > 0);

  // Borrador electrónico: se pide al abrir y cada vez que cambian las cantidades (con
  // un respiro para no pegarle al backend por cada tecla).
  useEffect(() => {
    if (!target || esOt || modo !== "electronica" || !clienteUnico) return undefined;
    const payload = payloadLinesDe();
    if (!payload.length) { setPrev(null); return undefined; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await backend.post("dte/previsualizar-desde-guias", { lines: payload });
        if (vivo) { setPrev({ ...data, _clave: JSON.stringify(payload) }); setPrevError(""); }
      } catch (e) {
        if (vivo) { setPrev(null); setPrevError(e?.response?.data?.error || e.message); }
      }
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, modo, qtyById, clienteUnico]);

  // El botón solo se habilita cuando el borrador corresponde a las cantidades actuales
  // (revisor 26-oct): la confirmación muestra el total del borrador, no otro.
  // Fuente "ot": no hay previsualizar-desde-guias (eso es para guías); el borrador se calcula
  // en el cliente con qty × unitPrice (neto redondeado por línea, IVA 19 %), igual que el modal
  // anterior de la OT.
  const borradorOt = useMemo(() => {
    if (!esOt || !otInfo) return null;
    const neto = lines.reduce((s, ln) => s + Math.round(Math.round(Number(qtyById[ln.deliveryId]) || 0) * (ln.unitPrice || 0)), 0);
    const iva = Math.round(neto * 0.19);
    return { neto, iva, total: neto + iva, receptor: otInfo.receptor, esProduccion: otInfo.esProduccion };
  }, [esOt, otInfo, lines, qtyById]);
  const borrador = esOt ? borradorOt : prev;
  const borradorVigente = esOt
    ? (!!borradorOt && borradorOt.total > 0)
    : (!!prev && prev._clave === JSON.stringify(payloadLinesDe()));
  const sinReceptorOt = esOt && !!otInfo && !otInfo.receptor;

  const { totalQty, totalMonto, anyOverflow } = useMemo(() => {
    let qty = 0, monto = 0, over = false;
    for (const ln of lines) {
      const q = Number(qtyById[ln.deliveryId] || 0);
      qty += q;
      monto += q * (ln.unitPrice || 0);
      if (q > ln.pendingQty || q < 0) over = true;
    }
    return { totalQty: qty, totalMonto: monto, anyOverflow: over };
  }, [lines, qtyById]);

  // Factura externa: cuánto cubre lo seleccionado respecto del total del documento.
  const diferenciaExt = esExterna && facturaExt ? (Number(facturaExt.montoTotal) || 0) - totalMonto : 0;

  // Agrupar por guía para mostrar bonito
  const linesByGuia = useMemo(() => {
    const m = new Map();
    for (const ln of lines) {
      const k = ln.guiaNumber;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(ln);
    }
    return m;
  }, [lines]);

  if (!target) return null;

  const emitirElectronica = async () => {
    setError("");
    if (anyOverflow) { setError("Hay cantidades fuera de rango (0 o mayor al pendiente)"); return; }
    const payloadLines = payloadLinesDe();
    if (payloadLines.length === 0) { setError("No hay líneas con cantidad > 0"); return; }
    if (sinReceptorOt) { setError("La OT no tiene cliente"); return; }
    if (!borrador || !borradorVigente) { setError(prevError || "Espera el borrador con las cantidades actuales antes de emitir"); return; }
    const real = borrador.esProduccion;
    if (real && !window.confirm(`Se va a emitir una FACTURA ELECTRÓNICA REAL a ${borrador.receptor?.name} por ${fmtCLP(borrador.total)} (IVA incluido). Un DTE no se borra: se corrige con nota de crédito.\n\n¿Emitir?`)) return;
    setSaving(true);
    try {
      if (esOt) {
        // La OT lleva su propia OC y archivo: dte/emitir toma la OC de la OT.
        setSavingStep(real ? "Emitiendo en el SII…" : "Emitiendo (prueba)…");
        const res = await backend.post("dte/emitir", {
          woNumber: target.woNumber,
          items: payloadLines.map(l => ({ piId: l.piId, qty: l.qty })),
          origen: "ot", tipoDoc: 33,
        });
        setEmitido(res.data);
        setSaving(false);
        setSavingStep("");
        return;
      }
      let ocDocumentId = null;
      // PC #8: en certificación no se sube nada a la OC real (la emisión de prueba no toca datos).
      if (ocFile && !real) setError("Ambiente de prueba: el archivo de la OC no se adjunta (se ignora).");
      if (ocFile && real) {
        setSavingStep("Subiendo archivo OC…");
        const firstCoId = lines.find(l => l.coId)?.coId;
        const form = new FormData();
        form.append("file", ocFile);
        form.append("docType", "ocCliente");
        if (clientOC.trim()) form.append("label", `OC ${clientOC.trim()}`);
        if (firstCoId) form.append("customerOrderRefs", firstCoId);
        const upRes = await backend.post("document/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        ocDocumentId = upRes.data?._id || null;
      }
      setSavingStep(real ? "Emitiendo en el SII…" : "Emitiendo (prueba)…");
      const res = await backend.post("dte/emitir-desde-guias", {
        lines: payloadLines, clientOCNumber: clientOC.trim() || null, ocDocumentId,
      });
      // El modal se queda abierto mostrando folio, PDF y avisos; onSaved (que cierra y
      // refresca Producción) se dispara recién con "Listo" (revisor 26-oct).
      setEmitido(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo emitir");
    }
    setSaving(false);
    setSavingStep("");
  };

  const abrirPdf = (folio) => {
    const w = window.open("", "_blank");
    backend.get(`dte/emitido/${folio}/pdf`, { params: { tipo: 33 }, responseType: "blob" })
      .then(r => { const u = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); if (w) w.location = u; else window.open(u, "_blank"); })
      .catch(() => { if (w) w.close(); setError("No se pudo abrir el PDF"); });
  };

  // Borrador PDF de la OT (GET dte/pre). Se abre la ventana antes del await por Safari iOS.
  // Ojo: el borrador del backend sale con el saldo de la OT, no con las cantidades editadas aquí.
  const verBorradorPdf = async () => {
    const w = window.open("", "_blank");
    try {
      const res = await backend.get(`dte/pre/factura/${target.woNumber}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (w) w.location = url; else window.location = url;
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) w.close();
      setError("No se pudo generar el borrador PDF. " + (e?.message || ""));
    }
  };

  const submit = async () => {
    setError("");
    if (!docNumber.trim()) { setError("N° factura requerido"); return; }
    if (docType === "nota_credito") { setError("Las notas de crédito aún no se vinculan desde este flujo"); return; }
    if (esExterna && !facturaExt?.clienteId) { setError("No se pudo identificar al cliente de la factura por RUT: complete el RUT en Contactos antes de vincular"); return; }
    if (anyOverflow) { setError("Hay cantidades fuera de rango (0 o mayor al pendiente)"); return; }
    const payloadLines = lines
      .map(ln => ({
        piId: ln.piId,
        deliveryId: ln.deliveryId,
        qty: Number(qtyById[ln.deliveryId] || 0),
      }))
      .filter(l => l.qty > 0);
    if (payloadLines.length === 0) { setError("No hay líneas con cantidad > 0"); return; }

    setSaving(true);
    try {
      // Si hay archivo de OC adjunto, subirlo primero. Lo vinculamos a la
      // primera CO como ref inicial — el endpoint de regularizar-factura
      // expande las refs al set completo de COs/WOs afectadas.
      let ocDocumentId = null;
      if (ocFile) {
        setSavingStep("Subiendo archivo OC…");
        const firstCoId = lines.find(l => l.coId)?.coId;
        const form = new FormData();
        form.append("file", ocFile);
        form.append("docType", "ocCliente");
        if (clientOC.trim()) form.append("label", `OC ${clientOC.trim()}`);
        if (firstCoId) form.append("customerOrderRefs", firstCoId);
        const upRes = await backend.post("document/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ocDocumentId = upRes.data?._id || null;
      }

      setSavingStep("Registrando factura…");
      const res = await backend.post("produccion/regularizar-factura", {
        clientOCNumber: clientOC.trim() || null,
        factura: {
          docType,
          docNumber: docNumber.trim(),
          docDate: docDate ? new Date(docDate).toISOString() : new Date().toISOString(),
        },
        lines: payloadLines,
        ocDocumentId,
        // Fuente "externa": el backend comprueba que la factura no esté ya vinculada, que el folio
        // calce y que las OT sean del mismo RUT; después le anota workOrder y repartos.
        ...(esExterna && facturaExt?._id ? { facturaId: facturaExt._id } : {}),
      });
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Error al registrar factura");
    }
    setSaving(false);
    setSavingStep("");
  };

  return ReactDOM.createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget && !saving) { if (emitido) onSaved?.(emitido); onClose?.(); } }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10001,
      }}
    >
      <div style={{
        background: "#1a1a1a", border: "1px solid #333", borderRadius: 8,
        width: "min(880px, 96vw)", maxHeight: isMobile ? "84vh" : "92vh", overflow: isMobile ? "auto" : "hidden",
        display: "flex", flexDirection: "column",
        color: "#ddd", fontSize: 12, fontFamily: "Arial, sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid #2e2e2e",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
            {esExterna ? `Vincular factura ${facturaExt?.folio ?? ""} a guías`
              : esOt ? `Facturar OT ${target?.woNumber ?? ""}`
              : modo === "electronica" ? "Emitir factura electrónica" : "Registrar folio externo"}
            <span style={{ fontSize: 11, color: "#8a8a8a", fontWeight: 400, marginLeft: 8 }}>
              {esOt
                ? `${lines.length} ítem${lines.length === 1 ? "" : "s"}${otInfo?.wo?.name || target?.nombre ? ` · ${otInfo?.wo?.name || target?.nombre}` : ""}`
                : `${lines.length} línea${lines.length === 1 ? "" : "s"} · ${linesByGuia.size} guía${linesByGuia.size === 1 ? "" : "s"}`}
            </span>
            {/* Ambiente SIEMPRE visible (fuente OT; en las otras llega con el borrador). */}
            {(esOt ? otInfo : (modo === "electronica" && prev)) && (
              <div style={{ fontSize: 11, marginTop: 3, fontWeight: 700, color: (esOt ? otInfo.esProduccion : prev.esProduccion) ? "#e74c3c" : "#f39c12" }}>
                {(esOt ? otInfo.esProduccion : prev.esProduccion) ? "⬤ PRODUCCIÓN — el documento es real" : "○ certificación (ambiente de prueba)"}
              </div>
            )}
          </div>
          <button
            onClick={() => { if (saving) return; if (emitido) onSaved?.(emitido); onClose?.(); }}
            style={{ background:"transparent", border:"none", color: "#cbd5e1", fontSize:20, cursor:"pointer", lineHeight:1 }}
          >×</button>
        </div>

        {/* Modo: electrónica (principal) · folio externo (secundaria, marcha blanca) */}
        {/* Fuente "ot": solo electrónica (una OT sin guía no tiene folio externo que registrar).
            Fuente "externa": el folio ya existe, solo se vincula. En ambas no hay chips. */}
        {!emitido && !esOt && !esExterna && (
          <div style={{ display: "flex", gap: 6, padding: "10px 18px 0", flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" onClick={() => !saving && setModo("electronica")} style={chip(modo === "electronica", "#2ecc71")}>
              ⚡ Emitir factura electrónica
            </button>
            <button type="button" onClick={() => !saving && setModo("externo")} style={chip(modo === "externo", "#f39c12")}>
              ✎ Registrar folio externo
            </button>
            <span style={{ fontSize: 10, color: "#777" }}>
              {modo === "electronica"
                ? "La emite el ERP por SimpleFactura y queda ligada a la OT y la OC."
                : "Para una factura ya hecha en Enternet (marcha blanca): solo se anota el número."}
            </span>
          </div>
        )}

        {emitido ? (
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
            <div style={{
              background: emitido.prueba ? "#2d2000" : "#0d2a15", border: `1px solid ${emitido.prueba ? "#4a3500" : "#0d4020"}`,
              borderRadius: 8, padding: "12px 14px", color: "#fff",
            }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                {emitido.prueba ? "○ Factura de PRUEBA emitida" : "✓ Factura electrónica emitida"} · Folio <span style={{ fontFamily: "monospace" }}>{emitido.folio ?? "—"}</span>
              </div>
              <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>
                {emitido.resumen?.receptor?.name || otInfo?.receptor?.name} · {emitido.resumen?.receptor?.rut || otInfo?.receptor?.rut} · neto {fmtCLP(emitido.neto)} · IVA {fmtCLP(emitido.iva)} · <strong style={{ color: "#fff" }}>{fmtCLP(emitido.total)}</strong>
              </div>
              {emitido.prueba && (
                <div style={{ fontSize: 11, color: "#f39c12", marginTop: 6 }}>
                  Ambiente de certificación: no es un documento real, no descuenta entregas ni toca la OT. Queda en la bitácora de emisiones.
                </div>
              )}
              {emitido.aplicado && emitido.aplicado.ok === false && (
                <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 6 }}>
                  ⚠ La factura salió pero las entregas no se aplicaron: {emitido.aplicado.error}. Quedó una alerta para revisar a mano.
                </div>
              )}
              {emitido.aplicado?.ok && (
                <div style={{ fontSize: 11, color: "#2ecc71", marginTop: 6 }}>
                  Aplicada a {emitido.aplicado.affectedPIs} pieza{emitido.aplicado.affectedPIs === 1 ? "" : "s"} de {emitido.aplicado.affectedWOs} OT: las guías pasan a facturadas y el folio queda en cada OT.
                </div>
              )}
              {esOt && emitido.cierreOt?.cerrada && (
                <div style={{ fontSize: 11, color: "#2ecc71", marginTop: 6 }}>
                  La OT quedó totalmente facturada y se cerró.
                </div>
              )}
              {esOt && emitido.cierreOt && !emitido.cierreOt.cerrada && emitido.cierreOt.motivo && (
                <div style={{ fontSize: 11, color: emitido.cierreOt.respaldo ? "#f39c12" : "#cbd5e1", marginTop: 6 }}>
                  La OT no se cerró: {emitido.cierreOt.motivo}.
                </div>
              )}
              {esOt && emitido.errorLibro && (
                <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 6 }}>
                  ⚠ La factura salió pero no quedó en el libro: {emitido.errorLibro}. Quedó una alerta para registrarla a mano.
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {emitido.folio && (
                  <button type="button" onClick={() => abrirPdf(emitido.folio)} style={botonSec("#3498db")}>📄 Ver PDF timbrado</button>
                )}
                <button type="button" onClick={() => { onSaved?.(emitido); onClose?.(); }} style={{ ...botonSec("#2ecc71"), background: "#2ecc71", color: "#0d2a15", fontWeight: 700 }}>Listo</button>
              </div>
            </div>
          </div>
        ) : (esOt || esExterna) && (cargando || cargaError || lines.length === 0) ? (
          <div style={{ padding: "18px" }}>
            {cargando ? (
              <div style={{ fontSize: 12, color: "#8a8a8a" }}>{esOt ? "Cargando la OT…" : "Buscando las guías pendientes del cliente…"}</div>
            ) : cargaError ? (
              <div style={aviso("#e74c3c")}>⛔ {cargaError}</div>
            ) : (
              <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                {esOt ? "Esta OT no tiene ítems con saldo por facturar." : "Este cliente no tiene guías entregadas por regularizar."}
              </div>
            )}
            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => onClose?.()} style={botonSec("#aaa")}>Cerrar</button>
            </div>
          </div>
        ) : (
        <>
        {/* Líneas */}
        <div style={{ flex: isMobile ? "0 0 auto" : 1, overflowY: isMobile ? "visible" : "auto", padding: "10px 18px" }}>
          <div style={{
            fontSize: 10, color: "#cbd5e1",
            textTransform: "uppercase", letterSpacing: ".06em",
            marginBottom: 8,
          }}>
            {esOt ? `Ítems a facturar (saldo por ítem) (${lines.length})`
              : esExterna ? `Guías pendientes del cliente (${lines.length} líneas)`
              : `Líneas seleccionadas (${lines.length})`}
          </div>

          {[...linesByGuia.entries()].map(([guia, lns]) => (
            <div key={guia} style={{ marginBottom: 12 }}>
              <div style={{
                display: "flex", gap: 8, alignItems: "center",
                padding: "4px 0", borderBottom: "1px solid #2a2a2a",
                marginBottom: 6,
              }}>
                <span style={{
                  fontFamily: "monospace", fontSize: 11, fontWeight: 700,
                  color: "#3498db", background: "#3498db18",
                  border: "1px solid #3498db44",
                  padding: "2px 7px", borderRadius: 4,
                }}>{esOt ? guia : `📄 Guía ${guia}`}</span>
                {!esOt && lns[0]?.coNumber && (
                  <span style={{
                    fontFamily: "monospace", fontSize: 11, fontWeight: 700,
                    color: "#f39c12", background: "#f39c1218",
                    border: "1px solid #f39c1244",
                    padding: "2px 7px", borderRadius: 4,
                  }}>OT {lns[0].coNumber}</span>
                )}
                {lns[0]?.existingClientOC && (
                  <span style={{ fontSize: 10, color: "#aaa" }}>
                    N° OC actual: <strong style={{ color: "#3498db" }}>{lns[0].existingClientOC}</strong>
                  </span>
                )}
                {esExterna && (
                  <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
                    <button type="button" disabled={saving} style={botonSec("#a78bfa")}
                      onClick={() => setQtyById(m => { const n = { ...m }; lns.forEach(l => { n[l.deliveryId] = l.pendingQty; }); return n; })}>
                      Toda la guía
                    </button>
                    <button type="button" disabled={saving} style={botonSec("#777")}
                      onClick={() => setQtyById(m => { const n = { ...m }; lns.forEach(l => { n[l.deliveryId] = 0; }); return n; })}>
                      Ninguna
                    </button>
                  </span>
                )}
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {!isMobile && <th style={th("18%")}>SKU</th>}
                    <th style={th("auto")}>Descripción</th>
                    {!isMobile && <th style={{ ...th("9%"), textAlign: "right" }}>Pend.</th>}
                    <th style={{ ...th(isMobile ? "72px" : "11%"), textAlign: "right" }}>{isMobile ? "Cant." : "A facturar"}</th>
                    {!isMobile && <th style={{ ...th("13%"), textAlign: "right" }}>$ Unit</th>}
                    <th style={{ ...th(isMobile ? "90px" : "14%"), textAlign: "right" }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {lns.map(ln => {
                    const q = Number(qtyById[ln.deliveryId] || 0);
                    const over = q > ln.pendingQty || q < 0;
                    return (
                      <tr key={ln.deliveryId}>
                        {!isMobile && (
                        <td style={{ ...td, fontFamily: "monospace", color: ln.sku ? "#3498db" : "#555" }}>
                          {ln.sku || "—"}
                        </td>
                        )}
                        <td style={{ ...td, color: "#fff", whiteSpace: "normal" }}>
                          {ln.name || <i style={{ color: "#999" }}>(sin descripción)</i>}
                          {isMobile && (
                            <div style={{ fontSize: 10, color: "#8a8a8a", fontFamily: "monospace" }}>
                              {ln.sku || "sin SKU"} · pend. {ln.pendingQty} · {fmtCLP(ln.unitPrice)} c/u
                            </div>
                          )}
                        </td>
                        {!isMobile && (
                        <td style={{ ...td, textAlign: "right", color: "#aaa" }}>
                          {ln.pendingQty}
                        </td>
                        )}
                        <td style={{ ...td, textAlign: "right" }}>
                          <input
                            type="number"
                            min="0"
                            max={ln.pendingQty}
                            value={qtyById[ln.deliveryId] ?? ""}
                            disabled={saving}
                            onChange={e => setQtyById(prev => ({
                              ...prev,
                              [ln.deliveryId]: e.target.value === "" ? "" : Number(e.target.value),
                            }))}
                            style={{
                              width: 60, padding: "3px 6px",
                              background: "#0d0d0d",
                              border: `1px solid ${over ? "#e74c3c" : "#444"}`,
                              borderRadius: 3,
                              color: over ? "#e74c3c" : "#fff",
                              fontSize: 11, textAlign: "right",
                              fontFamily: "monospace",
                            }}
                          />
                        </td>
                        {!isMobile && (
                        <td style={{ ...td, textAlign: "right", color: "#aaa" }}>
                          {fmtCLP(ln.unitPrice)}
                        </td>
                        )}
                        <td style={{ ...td, textAlign: "right", color: "#fff", fontWeight: 700 }}>
                          {fmtCLP(q * (ln.unitPrice || 0))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* Borrador electrónico: receptor, ambiente, totales, avisos */}
        {modo === "electronica" && (
          <div style={{ padding: "12px 18px", borderTop: "1px solid #2e2e2e", background: "#161616" }}>
            {esOt ? (
              !borradorOt ? (
                <div style={{ fontSize: 11, color: "#8a8a8a" }}>Armando el borrador…</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                  <div>
                    <div style={rotulo}>Receptor</div>
                    {borradorOt.receptor ? (
                      <>
                        <div style={{ color: "#fff", fontWeight: 700 }}>{borradorOt.receptor.name}</div>
                        <div style={{ fontFamily: "monospace", color: "#cbd5e1" }}>{borradorOt.receptor.rut || "SIN RUT"}</div>
                      </>
                    ) : (
                      <div style={aviso("#e74c3c")}>⚠ La OT no tiene cliente</div>
                    )}
                  </div>
                  <div>
                    <div style={rotulo}>Documento</div>
                    <div style={{ color: "#fff" }}>Factura electrónica (33) · OT {otInfo.wo?.number}</div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>{otInfo.wo?.clientOC ? `OC ${otInfo.wo.clientOC}` : "Sin N° de OC del cliente"}</div>
                    <button type="button" onClick={verBorradorPdf} disabled={saving}
                      style={{ ...botonSec("#3498db"), marginTop: 6, padding: "4px 10px", fontSize: 11 }}>
                      📄 Ver borrador PDF
                    </button>
                  </div>
                  <div>
                    <div style={rotulo}>Totales</div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>Neto <span style={{ color: "#fff" }}>{fmtCLP(borradorOt.neto)}</span></div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>IVA 19 % <span style={{ color: "#fff" }}>{fmtCLP(borradorOt.iva)}</span></div>
                    <div style={{ fontSize: 14, color: "#a78bfa", fontWeight: 800 }}>{fmtCLP(borradorOt.total)}</div>
                    <div style={{ fontSize: 10, color: "#777" }}>El folio lo asigna SimpleFactura al emitir.</div>
                  </div>
                </div>
              )
            ) : !clienteUnico ? (
              <div style={aviso("#e74c3c")}>
                ⛔ Las líneas son de clientes distintos ({clientes.join(" · ")}). Una factura tiene un solo receptor: elige líneas de un solo cliente o registra un folio externo.
              </div>
            ) : prevError ? (
              <div style={aviso("#e74c3c")}>⛔ {prevError}</div>
            ) : !prev ? (
              <div style={{ fontSize: 11, color: "#8a8a8a" }}>Armando el borrador…</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                <div>
                  <div style={rotulo}>Receptor</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{prev.receptor.name}</div>
                  <div style={{ fontFamily: "monospace", color: "#cbd5e1" }}>{prev.receptor.rut}</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {[prev.receptor.giro, prev.receptor.address, prev.receptor.comuna].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {prev.receptor.faltan?.length > 0 && (
                    <div style={{ fontSize: 10, color: "#f39c12", marginTop: 3 }}>
                      ⚠ Falta {prev.receptor.faltan.join(", ")} en Contactos: el SII los exige del receptor. Se emite igual con lo que hay.
                    </div>
                  )}
                </div>
                <div>
                  <div style={rotulo}>Documento</div>
                  <div style={{ color: "#fff" }}>Factura electrónica (33) · {prev.ots.length} OT · {prev.guias.length} guía{prev.guias.length === 1 ? "" : "s"}</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    OT {prev.ots.map(o => o.number).join(", ")}{prev.ocs.length ? ` · OC ${prev.ocs.join(", ")}` : ""}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 3, fontWeight: 700, color: prev.esProduccion ? "#e74c3c" : "#f39c12" }}>
                    {prev.esProduccion ? "⬤ PRODUCCIÓN — el documento es real" : "○ certificación (ambiente de prueba)"}
                  </div>
                </div>
                <div>
                  <div style={rotulo}>Totales</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>Neto <span style={{ color: "#fff" }}>{fmtCLP(prev.neto)}</span></div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>IVA 19 % <span style={{ color: "#fff" }}>{fmtCLP(prev.iva)}</span></div>
                  <div style={{ fontSize: 14, color: "#a78bfa", fontWeight: 800 }}>{fmtCLP(prev.total)}</div>
                  <div style={{ fontSize: 10, color: "#777" }}>El folio lo asigna SimpleFactura al emitir.</div>
                </div>
              </div>
            )}
            {!esOt && (
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
              <Field label="N° OC del cliente">
                <input className="bini-input bini-mono" value={clientOC} onChange={e => setClientOC(e.target.value)}
                  placeholder="opcional (aplica a todas)" disabled={saving} />
              </Field>
              <Field label="Adjuntar OC del cliente (opcional)">
                <input type="file" accept="application/pdf,image/*" disabled={saving}
                  onChange={e => setOcFile(e.target.files?.[0] || null)}
                  style={{ width: "100%", fontSize: 11, color: "#cbd5e1", background: "#0d0d0d", border: "1px solid #444", borderRadius: 3, padding: "5px 8px" }} />
              </Field>
            </div>
            )}
          </div>
        )}

        {/* Datos del DTE + clientOC (folio externo) */}
        {modo === "externo" && (
        <div style={{
          padding: "12px 18px",
          borderTop: "1px solid #2e2e2e",
          background: "#161616",
        }}>
          {esExterna && facturaExt && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 6 }}>
                {facturaExt.razonReceptor || facturaExt.clienteNombre || "Receptor sin nombre"}
                {facturaExt.rutReceptor ? ` · ${facturaExt.rutReceptor}` : ""}
              </div>
              <div style={{
                fontSize: 12, padding: "6px 10px", borderRadius: 4, fontWeight: 700,
                color: Math.round(diferenciaExt) === 0 ? "#2ecc71" : "#f39c12",
                background: Math.round(diferenciaExt) === 0 ? "#2ecc7118" : "#f39c1218",
                border: `1px solid ${Math.round(diferenciaExt) === 0 ? "#2ecc7144" : "#f39c1244"}`,
              }}>
                Factura {fmtCLP(facturaExt.montoTotal)} · seleccionado {fmtCLP(totalMonto)} · diferencia {fmtCLP(diferenciaExt)}
              </div>
              {clienteSinId && (
                <div style={{ ...aviso("#f39c12"), marginTop: 6 }}>
                  ⛔ No se pudo identificar al cliente de la factura por su RUT ({facturaExt.rutReceptor}). No se muestran guías: complete el RUT en Contactos y vuelva a intentar.
                </div>
              )}
              {docType === "nota_credito" && (
                <div style={{ ...aviso("#e74c3c"), marginTop: 6 }}>
                  ⛔ El documento es una nota de crédito: este flujo solo vincula facturas y boletas.
                </div>
              )}
            </div>
          )}

          {ocConflict && (
            <div style={{
              fontSize: 11, color: "#f39c12",
              marginBottom: 8,
              padding: "6px 10px", background: "#f39c1218",
              border: "1px solid #f39c1244", borderRadius: 4,
            }}>
              ⚠ Las líneas seleccionadas pertenecen a OCs con N° distintos. Si ingresa un N° OC se sobreescribirá en todas las OCs implicadas.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr", gap: 10 }}>
            <Field label="N° OC del cliente">
              <input
                className="bini-input bini-mono" value={clientOC}
                onChange={e => setClientOC(e.target.value)}
                placeholder="opcional (aplica a todas)"
                disabled={saving}
              />
            </Field>
            <Field label="Tipo">
              <select
                value={docType} onChange={e => setDocType(e.target.value)}
                className="bini-input" style={{ height: 32 }} disabled={saving || esExterna}
              >
                {docType === "nota_credito" && <option value="nota_credito">Nota de crédito</option>}
                <option value="factura">Factura</option>
                <option value="boleta">Boleta</option>
              </select>
            </Field>
            <Field label={`N° ${docType} *`}>
              <input
                className="bini-input bini-mono" value={docNumber}
                onChange={e => setDocNumber(e.target.value)}
                placeholder="ej: 4567"
                disabled={saving || esExterna}
              />
            </Field>
            <Field label="Fecha del DTE">
              <input
                type="date" className="bini-input" value={docDate}
                onChange={e => setDocDate(e.target.value)}
                disabled={saving || esExterna}
              />
            </Field>
          </div>

          {/* Archivo OC del cliente (respaldo) */}
          <div style={{ marginTop: 10 }}>
            <Field label="Adjuntar OC del cliente (PDF / imagen, opcional)">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  disabled={saving}
                  onChange={e => setOcFile(e.target.files?.[0] || null)}
                  style={{
                    flex: 1, fontSize: 11, color: "#cbd5e1",
                    background: "#0d0d0d",
                    border: "1px solid #444", borderRadius: 3,
                    padding: "5px 8px",
                  }}
                />
                {ocFile && (
                  <>
                    <span style={{ fontSize: 10, color: "#a78bfa", whiteSpace: "nowrap" }}>
                      📎 {ocFile.name}
                    </span>
                    <button
                      onClick={() => setOcFile(null)}
                      disabled={saving}
                      style={{
                        background: "transparent", border: "1px solid #444",
                        color: "#aaa", borderRadius: 3,
                        padding: "3px 8px", fontSize: 10, cursor: "pointer",
                      }}
                    >Quitar</button>
                  </>
                )}
              </div>
              <div style={{ fontSize: 10, color: "#777", marginTop: 4 }}>
                Si lo adjunta, el archivo queda como respaldo vinculado a todas las OCs facturadas.
              </div>
            </Field>
          </div>
        </div>
        )}

        {/* Footer con totales y acciones */}
        <div style={{
          padding: "12px 18px",
          borderTop: "1px solid #2e2e2e",
          background: "#141414",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ fontSize: 12, color: "#cbd5e1" }}>
            Total: <strong style={{ color: "#fff" }}>{totalQty}</strong> unidades ·{" "}
            <strong style={{ color: "#a78bfa" }}>{fmtCLP(totalMonto)}</strong>
          </div>
          {error && (
            <div style={{
              color:"#e74c3c", fontSize: 11,
              background:"rgba(231,76,60,0.08)", padding:"4px 10px", borderRadius: 4,
              border:"1px solid rgba(231,76,60,0.3)",
            }}>{error}</div>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              onClick={() => !saving && onClose?.()}
              disabled={saving}
              style={{
                background:"transparent", border:"1px solid #444",
                color:"#aaa", borderRadius: 5, padding:"7px 14px",
                fontSize: 12, cursor:"pointer",
              }}
            >Cancelar</button>
            {modo === "electronica" ? (
              <button
                onClick={emitirElectronica}
                disabled={saving || anyOverflow || !clienteUnico || !borradorVigente || sinReceptorOt}
                style={{
                  background: (anyOverflow || !clienteUnico || !borradorVigente || sinReceptorOt) ? "#444" : (borrador?.esProduccion ? "#e74c3c" : "#2ecc71"),
                  border:"none", color: borrador?.esProduccion ? "#fff" : "#0d2a15",
                  borderRadius: 5, padding:"7px 18px",
                  fontSize: 12, fontWeight: 700,
                  cursor: saving ? "wait" : ((anyOverflow || !clienteUnico || !borradorVigente || sinReceptorOt) ? "not-allowed" : "pointer"),
                  opacity: saving ? 0.7 : 1,
                }}
              >{saving ? (savingStep || "Emitiendo…") : borrador?.esProduccion ? "⚡ Emitir factura (REAL)" : "⚡ Emitir factura (prueba)"}</button>
            ) : (
            <button
              onClick={submit}
              disabled={saving || anyOverflow || docType === "nota_credito"}
              style={{
                background: (anyOverflow || docType === "nota_credito") ? "#444" : "#f39c12",
                border:"none", color:"#2d2000",
                borderRadius: 5, padding:"7px 18px",
                fontSize: 12, fontWeight: 700,
                cursor: saving ? "wait" : ((anyOverflow || docType === "nota_credito") ? "not-allowed" : "pointer"),
                opacity: saving ? 0.7 : 1,
              }}
            >{saving ? (savingStep || "Guardando…") : esExterna ? "Vincular factura a las guías" : `Registrar ${docType} externa`}</button>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>,
    document.body
  );
};

const chip = (activo, color) => ({
  background: activo ? `${color}22` : "transparent",
  border: `1px solid ${activo ? color : "#444"}`,
  color: activo ? color : "#aaa",
  borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer",
});
const botonSec = (color) => ({
  background: "transparent", border: `1px solid ${color}`, color, borderRadius: 5,
  padding: "7px 14px", fontSize: 12, cursor: "pointer",
});
const aviso = (color) => ({
  fontSize: 11, color, padding: "6px 10px", background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 4,
});
const rotulo = {
  fontSize: 9.5, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
};

const th = (width) => ({
  padding: "5px 6px",
  textAlign: "left",
  fontSize: 9,
  fontWeight: 700,
  color: "#aaa",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  borderBottom: "1px solid #2e2e2e",
  background: "#141414",
  whiteSpace: "nowrap",
  width,
});

const td = {
  padding: "5px 6px",
  fontSize: 11,
  color: "#bbb",
  borderBottom: "1px solid #1f1f1f",
};

const Field = ({ label, children }) => (
  <div>
    <div style={{
      fontSize: 9.5, fontWeight: 700, color: "#cbd5e1",
      textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
    }}>{label}</div>
    {children}
  </div>
);

export default FacturarModal;
